import React, { LazyExoticComponent, FC, ReactNode } from "react";
import { Translation } from "react-i18next";

export type Route = {
  element?: LazyExoticComponent<FC>;
  exact?: boolean;
  name?: ReactNode;
  path?: string;
  routes?: Route[];
  hideSidebar?: boolean;
  fullWidth?: boolean;
};

const OnboardingWizard = React.lazy(
  () => import("./views/settings/OnboardingWizard"),
);

const Dashboard = React.lazy(() => import("./views/dashboard/Dashboard"));

const PropertyList = React.lazy(
  () => import("./views/properties/PropertyList"),
);

const PropertyForm = React.lazy(
  () => import("./views/properties/PropertyForm"),
);

const UnitList = React.lazy(() => import("./views/properties/UnitList"));

const UnitForm = React.lazy(() => import("./views/properties/UnitForm"));

const SDAForm = React.lazy(() => import("./views/sda/SDAForm"));

const InviteList = React.lazy(() => import("./views/sda/InviteList"));

const PolicyList = React.lazy(() => import("./views/policies/PolicyList"));

const PolicyDetails = React.lazy(
  () => import("./views/policies/PolicyDetails"),
);

const MoveOutTenant = React.lazy(
  () => import("./views/policies/MoveOutTenant"),
);

const RenewalList = React.lazy(() => import("./views/policies/RenewalList"));

const RenewalForm = React.lazy(() => import("./views/policies/RenewalForm"));

const ClaimList = React.lazy(() => import("./views/claims/ClaimList"));

const StripeConnect = React.lazy(
  () => import("./views/settings/StripeConnect"),
);

const ClaimForm = React.lazy(() => import("./views/claims/ClaimForm"));

const CollectionList = React.lazy(
  () => import("./views/collections/CollectionList"),
);

const CollectionForm = React.lazy(
  () => import("./views/collections/CollectionForm"),
);

const CollectionBulkUpload = React.lazy(
  () => import("./views/collections/CollectionBulkUpload"),
);

const ApplicationList = React.lazy(
  () => import("./views/applications/ApplicationList"),
);

const ApplicationForm = React.lazy(
  () => import("./views/applications/ApplicationForm"),
);

const ApplicationDetails = React.lazy(
  () => import("./views/applications/ApplicationDetails"),
);

const Settings = React.lazy(() => import("./views/settings/Settings"));

const routes: Route[] = [
  {
    path: "/",
    exact: true,
    name: <Translation>{(t) => t("Home")}</Translation>,
  },

  {
    path: "/onboarding",
    exact: true,
    name: <Translation>{(t) => t("Onboarding")}</Translation>,
    element: OnboardingWizard,
    hideSidebar: true,
  },

  {
    path: "/dashboard",
    name: <Translation>{(t) => t("Dashboard")}</Translation>,
    element: Dashboard,
    fullWidth: true,
  },

  {
    path: "/applications",
    name: <Translation>{(t) => t("Applications")}</Translation>,
    element: ApplicationList,
    fullWidth: true,
  },
  {
    path: "/applications/:uuid/details",
    name: <Translation>{(t) => t("Application")}</Translation>,
    element: ApplicationDetails,
  },
  {
    path: "/applications/new",
    name: <Translation>{(t) => t("New Application")}</Translation>,
    element: ApplicationForm,
  },
  {
    path: "/applications/:uuid/edit",
    name: <Translation>{(t) => t("Edit Application")}</Translation>,
    element: ApplicationForm,
  },

  {
    path: "/properties",
    name: <Translation>{(t) => t("Properties")}</Translation>,
    element: PropertyList,
    fullWidth: true,
  },
  {
    path: "/properties/new",
    name: <Translation>{(t) => t("New Property")}</Translation>,
    element: PropertyForm,
  },
  {
    path: "/properties/:uuid/edit",
    name: <Translation>{(t) => t("Edit Property")}</Translation>,
    element: PropertyForm,
  },
  {
    path: "/properties/:propertyUuid/units",
    name: <Translation>{(t) => t("Units")}</Translation>,
    element: UnitList,
    fullWidth: true,
  },
  {
    path: "/properties/:propertyUuid/units/new",
    name: <Translation>{(t) => t("Add Unit")}</Translation>,
    element: UnitForm,
  },
  {
    path: "/properties/:propertyUuid/units/:unitUuid/edit",
    name: <Translation>{(t) => t("Edit Unit")}</Translation>,
    element: UnitForm,
  },

  {
    path: "/invitations",
    name: <Translation>{(t) => t("Invitations")}</Translation>,
    element: InviteList,
    fullWidth: true,
  },
  {
    path: "/sda/new",
    name: <Translation>{(t) => t("New Invite")}</Translation>,
    element: SDAForm,
  },
  {
    path: "/sda/:uuid/edit",
    name: <Translation>{(t) => t("Edit Invite")}</Translation>,
    element: SDAForm,
  },

  {
    path: "/tenants",
    name: <Translation>{(t) => t("Tenants")}</Translation>,
    element: PolicyList,
    fullWidth: true,
  },
  {
    path: "/tenants/:uuid",
    name: <Translation>{(t) => t("Tenant Details")}</Translation>,
    element: PolicyDetails,
  },
  {
    path: "/policies/move-out",
    name: <Translation>{(t) => t("Move Out Tenant")}</Translation>,
    element: MoveOutTenant,
  },
  {
    path: "/policies/move-out/:uuid",
    name: <Translation>{(t) => t("Move Out Tenant")}</Translation>,
    element: MoveOutTenant,
  },

  {
    path: "/renewals",
    name: <Translation>{(t) => t("Renewals")}</Translation>,
    element: RenewalList,
    fullWidth: true,
  },
  {
    path: "/renewals/:uuid/confirm",
    name: <Translation>{(t) => t("Confirm Renewal")}</Translation>,
    element: RenewalForm,
  },

  {
    path: "/claims",
    name: <Translation>{(t) => t("Claims")}</Translation>,
    element: ClaimList,
    fullWidth: true,
  },
  {
    path: "/claims/new",
    name: <Translation>{(t) => t("New Claim")}</Translation>,
    element: ClaimForm,
  },
  {
    path: "/claims/:uuid/edit",
    name: <Translation>{(t) => t("Edit Claim")}</Translation>,
    element: ClaimForm,
  },

  {
    path: "/stripe-connect",
    name: <Translation>{(t) => t("Payout Setup")}</Translation>,
    element: StripeConnect,
  },

  {
    path: "/collections",
    name: <Translation>{(t) => t("Collections")}</Translation>,
    element: CollectionList,
    fullWidth: true,
  },
  {
    path: "/collections/new",
    name: <Translation>{(t) => t("New Collection")}</Translation>,
    element: CollectionForm,
  },
  {
    path: "/collections/:uuid/edit",
    name: <Translation>{(t) => t("Edit Collection")}</Translation>,
    element: CollectionForm,
  },
  {
    path: "/collections/bulk-upload",
    name: <Translation>{(t) => t("Bulk Upload Collections")}</Translation>,
    element: CollectionBulkUpload,
  },

  {
    path: "/settings",
    name: <Translation>{(t) => t("Settings")}</Translation>,
    element: Settings,
  },
];

export default routes;
